import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import type { PAGE_TYPES } from 'src/constants/module';
import type { SimpleContentPage } from 'src/types/customizing';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

const useSimpleContentPage = (pageTye: PAGE_TYPES) => {
  const { i18n, t } = useTranslation('common');

  const endPoint = useMemo(
    () =>
      generatePath(apiPaths.CUSTOMIZING.SIMPLE_CONTENT_PAGE_BY_TYPE, {
        page_type: pageTye,
      }),
    [pageTye],
  );

  const { data, error, isFetching, isLoading } = useQuery<string, string>({
    queryKey: [endPoint, i18n.language],
    queryFn: () =>
      HttpService.get(endPoint)
        .then(
          ({ data }: AxiosResponse<SimpleContentPage>) => data?.content || '',
        )
        .catch(({ response }) => {
          const errorMsg = response?.data?.detail || t('networkError');
          return Promise.reject(errorMsg || 'An unknown error occurred.');
        }),
  });

  const pageContent = useMemo(() => data || '', [data]);
  const loading = isFetching || isLoading;

  return { pageContent, error, loading };
};

export default useSimpleContentPage;
